import React, { memo, useLayoutEffect, useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { DesignerGameStatusHelperText, DesignerGameStatusToLabel } from "@seabrookstudios/pitch2table-core";
import { Path } from "../Path";
import EmojiObjects from "@mui/icons-material/EmojiObjects";
import Engineering from "@mui/icons-material/Engineering";
import ViewInAr from "@mui/icons-material/ViewInAr";
import Balance from "@mui/icons-material/Balance";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import { CoverSquare } from "./CoverImages";
import Article from "@mui/icons-material/Article";
import RecordVoiceOver from "@mui/icons-material/RecordVoiceOver";
import Storefront from "@mui/icons-material/Storefront";

/**
 * @type {import("@seabrookstudios/pitch2table-core").DesignerGameStatus[]}
 */
export const GAME_LIFE_CYCLE_SEQUENCE = [
  "Idea",
  "InDevelopmentEarly",
  "InDevelopmentMid",
  "InDevelopmentLate",
  "PitchingNow",
  "Signed",
  "Published",
];

/**
 * @type {Record<import("@seabrookstudios/pitch2table-core").DesignerGameStatus, import("@mui/icons-material").SvgIconComponent>}
 */
const LifeCycleIcons = {
  Idea: EmojiObjects,
  InDevelopmentEarly: Engineering,
  InDevelopmentMid: ViewInAr,
  InDevelopmentLate: Balance,
  PitchingNow: RecordVoiceOver,
  Signed: Article,
  Published: Storefront,
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").DesignerGameStatus} status
 */
export const getLifecycleProgressIndex = (status) => {
  const direct = GAME_LIFE_CYCLE_SEQUENCE.indexOf(status);
  if (direct >= 0) {
    return direct;
  }
  return 0;
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary[]} games
 * @returns {import("@seabrookstudios/pitch2table-core").GameSummary[]}
 */
export const sortGamesByLifecycleProgress = (games) => {
  return [...games].sort((a, b) => {
    const progressDiff = getLifecycleProgressIndex(b.status) - getLifecycleProgressIndex(a.status);
    if (progressDiff !== 0) {
      return progressDiff;
    }
    return a.name.localeCompare(b.name);
  });
};

/** Rows mounted per animation frame after the first batch (keeps main thread responsive for large lists). */
const ROWS_PER_FRAME = 28;

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary} props.game
 */
function GameLifecycleTableRowBase({ game }) {
  const progressIndex = getLifecycleProgressIndex(game.status);

  return (
    <TableRow
      hover
      component={RouterLink}
      to={Path.user().game(game.id)}
      sx={{
        cursor: "pointer",
        color: "inherit",
        textDecoration: "none",
        "&:hover": { color: "inherit" },
      }}
    >
      <TableCell sx={{ width: 56, verticalAlign: "middle", py: 1, pr: 0 }}>
        <CoverSquare game={game} width={48} height={48} loading="lazy" />
      </TableCell>
      <TableCell sx={{ verticalAlign: "middle" }}>
        <Typography variant="body2" fontWeight={500}>
          {game.name}
        </Typography>
        {game.tag ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {game.tag}
          </Typography>
        ) : null}
      </TableCell>
      {GAME_LIFE_CYCLE_SEQUENCE.map((status, index) => {
        const Icon = LifeCycleIcons[status];
        const isComplete = progressIndex >= index;
        const label = DesignerGameStatusToLabel[status];
        const helper = DesignerGameStatusHelperText[status];
        return (
          <TableCell align="center" key={status} sx={{ px: 0.5 }}>
            <Tooltip title={`${label}. ${helper}`}>
              <Icon fontSize="small" color={isComplete ? "primary" : "disabled"} aria-label={label} />
            </Tooltip>
          </TableCell>
        );
      })}
      <TableCell align="center">
        <ArrowForwardOutlined fontSize="small" color="action" />
      </TableCell>
    </TableRow>
  );
}

const GameLifecycleTableRow = memo(GameLifecycleTableRowBase);

/**
 * Renders the lifecycle progress table for an arbitrary list of games (sorting and chunked mount are internal).
 *
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary[]} props.games games to show (e.g. already narrowed by filter)
 */
export const GameLifecycleTableDisplay = ({ games }) => {
  const sortedGames = useMemo(() => sortGamesByLifecycleProgress(games), [games]);

  const [visibleRowCount, setVisibleRowCount] = useState(0);

  useLayoutEffect(() => {
    const total = sortedGames.length;
    if (total === 0) {
      setVisibleRowCount(0);
      return undefined;
    }

    let cancelled = false;
    let visible = Math.min(ROWS_PER_FRAME, total);
    setVisibleRowCount(visible);

    const step = () => {
      if (cancelled) {
        return;
      }
      if (visible >= total) {
        return;
      }
      visible = Math.min(visible + ROWS_PER_FRAME, total);
      setVisibleRowCount(visible);
      if (visible < total) {
        requestAnimationFrame(step);
      }
    };

    if (visible < total) {
      requestAnimationFrame(step);
    }

    return () => {
      cancelled = true;
    };
  }, [sortedGames]);

  const visibleGames = useMemo(() => sortedGames.slice(0, visibleRowCount), [sortedGames, visibleRowCount]);

  const listStillMounting = sortedGames.length > 0 && visibleRowCount < sortedGames.length;

  return (
    <Stack spacing={0} px={2} pb={2}>
      <TableContainer aria-busy={listStillMounting}>
        <Table size="small">
          <TableBody>
            {visibleGames.map((game) => (
              <GameLifecycleTableRow key={game.id} game={game} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
